import {
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControl,
  ListItem,
  Paper,
  TextField,
  Typography
} from "@mui/material";
import { ConfirmationModal, useToast } from "@qubit/autoparts";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { AddOrderTypeDialog } from "~/components/orderTypes/AddOrderTypeDialog";
import {
  clearOrderTypeToDelete,
  closeAddOrderTypeDialog,
  openAddOrderTypeDialog,
  selectIsAddOrderTypeDialogOpen,
  selectOrderTypeToDelete
} from "~/components/orderTypes/orderTypesDropdown.slice";
import {
  newSettingsAutocompleteTextFieldSx,
  overviewAutocompleteSx,
  overviewAutocompleteTagsDisplaySx
} from "~/features/preview/workstation/workstation.styles";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import {
  useDeleteOrderTypeMutation,
  useGetOrderTypesQuery
} from "~/redux/warehouse/orders.hooks";

type Props<T extends FieldValues> = {
  control: Control<T>;
  fieldName: Path<T>;
};

export function OrderTypeSelect<T extends FieldValues>(props: Props<T>) {
  const { control, fieldName } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { successToast, errorToast } = useToast();
  const isAddOrderTypeDialogOpen = useAppSelector(
    selectIsAddOrderTypeDialogOpen
  );
  const orderTypeToDelete = useAppSelector(selectOrderTypeToDelete);
  const [deleteOrderType] = useDeleteOrderTypeMutation();

  const { orderTypes } = useGetOrderTypesQuery(undefined, {
    refetchOnMountOrArgChange: true,
    selectFromResult: ({ data }) => ({
      orderTypes: data ? data.map((orderTypeDto) => orderTypeDto.orderType) : []
    })
  });

  const handleAddNewOrderTypeClick = () => {
    dispatch(openAddOrderTypeDialog());
  };

  const handleCloseDeleteModal = () => {
    dispatch(clearOrderTypeToDelete());
    dispatch(closeAddOrderTypeDialog());
  };

  const handleConfirmDeleteOrderType = async () => {
    /* istanbul ignore next */
    if (!orderTypeToDelete) return;
    try {
      await deleteOrderType({
        orderTypeId: orderTypeToDelete?.orderTypeId
      }).unwrap();
      successToast(t("order type deleted"));
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
    } finally {
      handleCloseDeleteModal();
    }
  };

  return (
    <Box width="100%">
      <FormControl fullWidth size="small">
        <Controller
          name={fieldName}
          control={control}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              multiple
              size="small"
              autoComplete={false}
              disableClearable
              disableCloseOnSelect
              aria-label={t("order types overview aria label")}
              getOptionLabel={(orderType) => orderType}
              options={orderTypes}
              sx={overviewAutocompleteSx}
              renderOption={(props, option, state) => (
                <ListItem {...props} key={`${state.index}-${option}`}>
                  <Typography sx={{ width: "100%" }}>{option}</Typography>
                </ListItem>
              )}
              renderTags={(tagValue) => (
                <Box component="span" sx={overviewAutocompleteTagsDisplaySx}>
                  {tagValue.join(", ")}
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  slotProps={{
                    htmlInput: {
                      ...params.inputProps,
                      "aria-label": t("order types overview aria label"),
                      readOnly: true
                    }
                  }}
                  sx={newSettingsAutocompleteTextFieldSx}
                />
              )}
              value={value ?? []}
              onChange={(_, values) => onChange(values)}
              slots={{
                paper: ({ children }) => {
                  return (
                    <Paper onMouseDown={(event) => event.preventDefault()}>
                      {children}
                      <Divider variant="middle" />
                      <Button
                        fullWidth
                        sx={{
                          justifyContent: "flex-start",
                          my: 1
                        }}
                        onClick={handleAddNewOrderTypeClick}
                      >
                        {t("add new order type")}
                      </Button>
                    </Paper>
                  );
                }
              }}
            />
          )}
        />
      </FormControl>

      {isAddOrderTypeDialogOpen && <AddOrderTypeDialog />}

      {orderTypeToDelete && (
        <ConfirmationModal
          isOpen
          confirmCb={handleConfirmDeleteOrderType}
          closeCb={handleCloseDeleteModal}
          modalTitle={t("delete order type")}
          modalText={`${t("are you sure you want to delete the order type")}: "${
            orderTypeToDelete.orderType
          }"?`}
          labelledById="delete-order-type-modal-title"
        />
      )}
    </Box>
  );
}
